import React from "react";
import { disableBodyScroll, enableBodyScroll, clearAllBodyScrollLocks } from "body-scroll-lock";
import { useEffect } from "react";
import { useState } from "react";
import { constants } from "../../constants";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/pro-light-svg-icons";
import { FileReference } from './FileUpload';
import { ModalWindow } from "../utilities/ModalWindow";

type BannerLibraryPickerProps = {
    currentBackground: FileReference | null;
    onSelect: (selectedImage: string) => void;
};

export default function BannerLibraryPicker({
    currentBackground,
    onSelect,
}: BannerLibraryPickerProps) {
    const [open, setOpen] = useState(false);
    const [selected, setSelected] = useState<string | null>(null);

    const images = React.useMemo<string[]>(
        () => [
            "spaces/banners/lual_banner_angel.jpeg",
            "spaces/banners/lual_banner_forest.jpeg",
            "spaces/banners/lual_banner_balloons.jpeg",
            "spaces/banners/lual_banner_sakura.jpeg",
            "spaces/banners/lual_banner_flowers.jpeg",
            "spaces/banners/lual_banner_colors.jpeg",
            "spaces/banners/lual_banner_aircraft.jpeg",
            "spaces/banners/lual_banner_colors_2.jpeg",
        ],
        []
    );

    // Show/hide the scroll bar whenever the dialog is open or closed
    useEffect(() => {
        const modalWindowElement = document.querySelector('#modalWindow');
        if (modalWindowElement != null) {
            if (open)
                disableBodyScroll(modalWindowElement, { reserveScrollBarGap: true });
            else
                enableBodyScroll(modalWindowElement);
        } else if (!open)
            clearAllBodyScrollLocks();
    }, [open]);

    useEffect(() => {
        if (currentBackground) {
            const filename = currentBackground.hostFilename;
            if (filename && !images.includes(filename)) {
                setSelected(null);
            } else if (filename && images.includes(filename)) {
                setSelected(filename);
            }
        }
    }, [currentBackground, images]);

    const onSelection = (target: string) => {
        setSelected(target);
    };

    const submitHandler = () => {
        if (selected) {
            onSelect(selected);
            setOpen(false);
        }
    };

    return (
        <div>
            <button type='button' className='btn' onClick={() => setOpen(true)}>
                Choose from a library
            </button>

            {open && (
                <ModalWindow>
                    <div className='p-4 md:p-8'>
                        <div className="flex mb-4 md:-mt-2">
                            <h3 className="flex-1 text-2xl font-medium text-gray-800">
                                Select an image
                            </h3>
                            <button onClick={() => setOpen(false)}>
                                <FontAwesomeIcon
                                    icon={faTimes}
                                    size="lg"
                                    className="text-2xl text-gray-500 hover:text-red-400 transition-colors ease-out"
                                />
                            </button>
                        </div>
                        <p className="max-w-prose text-gray-600 mb-8">
                            If you don&apos;t want to upload your own banner
                            image, you can choose from one of our defaults
                            below.
                        </p>
                        <div className="bg-gray-900 -mx-8 px-8 py-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                            {images.map((image) => (
                                <img
                                    key={image}
                                    src={`${constants.cdnBaseUrl}/${image}`}
                                    className={`filter w-full h-40 object-cover border-2 border-gray-800 hover:border-gray-300 brightness-90 hover:brightness-105 transition ease-out duration-300 cursor-pointer ${
                                        selected === image
                                            ? "border-yellow-400 hover:border-yellow-400 brightness-100"
                                            : ""
                                    }`}
                                    onClick={() => onSelection(image)}
                                    alt=''
                                />
                            ))}
                        </div>
                        <div className="mt-8 mb-4 md:mb-0 flex justify-end">
                            <button
                                className="btn"
                                onClick={() => setOpen(false)}>
                                Cancel
                            </button>
                            <button
                                className="btn btn-primary ml-2"
                                onClick={submitHandler}
                                disabled={!selected}>
                                Select
                            </button>
                        </div>
                    </div>
                </ModalWindow>
            )}
        </div>
    );
}
